import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { canAccessPlatform } from "../utils/permissions";
import {
  ToggleLeft,
  ToggleRight,
  SpinnerGap,
  ArrowClockwise,
  Lightning,
  Info,
  CheckCircle,
  XCircle
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

export default function FeatureFlagsPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  const loadFlags = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/platform/feature-flags`, { withCredentials: true });
      setFlags(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load feature flags:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFlags(); }, []);

  const toggleFlag = async (flag) => {
    setToggling(flag.id);
    try {
      await axios.put(`${API}/api/platform/feature-flags/${flag.id}`, {
        enabled_globally: !flag.enabled_globally
      }, { withCredentials: true });
      setFlags(prev => prev.map(f => f.id === flag.id ? { ...f, enabled_globally: !f.enabled_globally } : f));
    } catch (err) {
      console.error("Failed to toggle flag:", err);
    } finally {
      setToggling(null);
    }
  };

  if (!canAccessPlatform(user) && user?.role !== "admin") {
    return <div className="p-8 text-center text-muted-foreground">Acces refuse</div>;
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="feature-flags-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-600/20 border border-violet-500/20 flex items-center justify-center">
            <Lightning size={22} className="text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit'] tracking-tight" data-testid="feature-flags-title">{t("flags_title")}</h1>
            <p className="text-xs text-muted-foreground">{flags.length} {t("flags_subtitle")}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadFlags} className="gap-2" data-testid="refresh-flags-btn">
          <ArrowClockwise size={14} />
          Rafraichir
        </Button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-violet-500/5 border border-violet-500/15 rounded-xl">
        <Info size={18} className="text-violet-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-muted-foreground">
          <p>Les <strong className="text-foreground">feature flags</strong> permettent d'activer ou désactiver des fonctionnalites pour l'ensemble de la plateforme sans deploiement. Les overrides par tenant sont disponibles via l'API.</p>
        </div>
      </div>

      {/* Flags Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <SpinnerGap size={28} className="animate-spin text-violet-400" />
        </div>
      ) : flags.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Lightning size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">{t("no_flags")}</p>
          <p className="text-sm mt-1">Les feature flags seront crees automatiquement lors du seed initial.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="flags-grid">
          {flags.map((flag) => (
            <div
              key={flag.id}
              className={`bg-card border rounded-xl p-5 transition-all ${
                flag.enabled_globally
                  ? "border-teal-500/20 shadow-[0_0_15px_rgba(16,185,129,0.05)]"
                  : "border-border"
              }`}
              data-testid={`flag-card-${flag.id || flag.code}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm font-['Outfit'] truncate">{flag.name || flag.code}</h3>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      flag.enabled_globally
                        ? "bg-teal-500/10 text-teal-400 border border-teal-500/20"
                        : "bg-secondary text-muted-foreground border border-border"
                    }`}>
                      {flag.enabled_globally ? (
                        <><CheckCircle size={10} weight="fill" /> Actif</>
                      ) : (
                        <><XCircle size={10} /> Inactif</>
                      )}
                    </span>
                  </div>
                  <code className="text-[11px] text-muted-foreground font-mono mt-1 block">{flag.code}</code>
                  {flag.description && (
                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{flag.description}</p>
                  )}
                </div>
                <button
                  onClick={() => toggleFlag(flag)}
                  disabled={toggling === flag.id}
                  className="ml-4 flex-shrink-0 transition-colors"
                  data-testid={`toggle-flag-${flag.id || flag.code}`}
                >
                  {toggling === flag.id ? (
                    <SpinnerGap size={28} className="animate-spin text-muted-foreground" />
                  ) : flag.enabled_globally ? (
                    <ToggleRight size={32} weight="fill" className="text-teal-400" />
                  ) : (
                    <ToggleLeft size={32} className="text-muted-foreground" />
                  )}
                </button>
              </div>
              {flag.updated_at && (
                <p className="text-[10px] text-muted-foreground mt-3 border-t border-border pt-2">
                  {t("last_modified")}: {new Date(flag.updated_at).toLocaleString("fr-FR")}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
